import { Checkbox, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, OutlinedInput, Radio, RadioGroup, Select, Stack, TextField, Typography } from "@mui/material"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Pagination from "@mui/material/Pagination"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TablePagination from "@mui/material/TablePagination"
import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react"
import { OrderContext } from "../providers/OrderProvider"
import { Order, OrderOrderStatusEnum } from "@sb1finans/new-api/dist/api"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { formatCurrency } from "@sb1finans/components/dist/FormattedCurrency"
import { SelectChangeEvent } from "@mui/material/Select"

function createData(
    name: string,
    debtor: string,
    dueDate: string,
    amount: string,
    status: string,
  ) {
    return { name, debtor, dueDate, amount, status };
  }

const OrderList = () => {

    const formatOrderStatus = (status: string): string => {
        switch (status.toUpperCase()) {
            /*case "PENDING":
                return "Under behandling"
            case "NEW":
                return "Opprettet"
            case "CANCELLED":
                return "Avbrutt"
            case "DELETED":
                return "Slettet"*/
            case "COMPLETED":
                return "Finansiert"
            /*case "REJECTED":
                return "Avvist"
            case "SIGNATURE_COMPLETE":
                return "Signert"*/
            case "ADMINISTERED":
              return "Administrert"
            default:
              return "Under behandling"
        }
    }

    const statuses = (Object.keys(OrderOrderStatusEnum) as Array<keyof typeof OrderOrderStatusEnum>).map((key) => formatOrderStatus(key))
    const statusFilters = statuses.filter((val, idx, arr) => arr.indexOf(val) === idx)

    const {orders, filter, fetchOrders} = useContext(OrderContext)

    const [fromDate, setFromDate] = useState(filter.fromDate)
    const [toDate, setToDate] = useState(filter.toDate)
    const [maxCount, setMaxCount] = useState(10)
    const [currentPage, setCurrentPage] = useState(0)
    const [activeFilters, setActiveFilters] = useState<string[]>(statusFilters)

    useEffect(() => {
      fetchOrders({fromDate, toDate})
    }, [fromDate, toDate, maxCount, currentPage])

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      setCurrentPage(page)
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setMaxCount(parseInt(event.target.value, 10))
    }

    const mapToDescription = (order: any) => {
      const description = order.orderLines[0] ?? ""
      return order.paymentMethods[0].paymentType == "INVOICE" ? `Faktura nr: ${order.externalId}` : description
    }

    const mapToCustomerName = (billingAddress: any) => {
        if (billingAddress) {
          return billingAddress.legalId?.length == 9 ? billingAddress.companyName : billingAddress.firstName + " " + billingAddress.lastName
        } else {
          return ""
        }
    }

    const setFromDateValue = (value: any) => {
      setFromDate(value)
      setCurrentPage(0)
    }

    const setToDateValue = (value: any) => {
      setToDate(value)
      setCurrentPage(0)
    }

    const onFilterSelected = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      console.log("event: ", event.target.name, "checked: ", checked)
      if (checked) {
        setActiveFilters([...activeFilters, event.target.name])
      } else {
        setActiveFilters(activeFilters.filter((val) => val !== event.target.name))
      }
      setCurrentPage(0)
    }

    console.log(activeFilters)
    const rows = useMemo(() => {
      return orders
        .sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime())
        .map(order => createData(
          mapToDescription(order),
          mapToCustomerName(order.billingAddress), 
          new Date(order.paymentDueDate).toLocaleDateString("no-NO"), 
          formatCurrency(order.totalAmount / 100), 
          formatOrderStatus(order.orderStatus)
        ))
        .filter((row) => activeFilters.includes(row.status))
    }, [fromDate, toDate, orders, activeFilters])
    
    const currentRows = rows && rows.slice(currentPage * maxCount, (currentPage + 1) * maxCount)

    return (
      <Grid container spacing={2} justifyContent="center" sx={{marginTop: "40px"}}>
        {/*<Grid item xs={12}>
          <OrderChart orders={orders} />
    </Grid>*/}
        <Grid item xs={10}>
            <Box component={Paper} sx={{padding: "16px 24px"}}>
                <Typography variant="h4" sx={{padding: "16px 0px"}}>Fakturaoversikt</Typography>
                <Stack direction="row">
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          label="Fra dato"
                          value={fromDate}
                          onChange={(newValue) => {
                            setFromDateValue(newValue);
                          }}
                          renderInput={(params) => <TextField sx={{margin: "8px"}} {...params} />}
                        />
                     </LocalizationProvider>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            label="Til dato"
                            value={toDate}
                            onChange={(newValue) => {
                              setToDateValue(newValue);
                            }}
                            renderInput={(params) => <TextField sx={{margin: "8px"}} {...params} />}
                          />
                       </LocalizationProvider>
                    <FormControl sx={{ m: 1, width: 300, marginLeft: "auto" }}>
                        <InputLabel id="demo-multiple-checkbox-label">Statusfilter</InputLabel>
                        <Select
                          labelId="demo-multiple-checkbox-label"
                          id="demo-multiple-checkbox"
                          multiple
                          value={statusFilters}
                          input={<OutlinedInput label="Status" />}
                          renderValue={(selected) => "Statusfilter"}
                        >
                          {statusFilters.map((name) => (
                            <MenuItem key={name} value={name}>
                              <Checkbox name={name} checked={activeFilters.indexOf(name) > -1} onChange={onFilterSelected} />
                              <ListItemText primary={name} />
                            </MenuItem>
                          ))}
                        </Select>
                    </FormControl>
                </Stack>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Beskrivelse</TableCell>
                    <TableCell align="right">Kunde</TableCell>
                    <TableCell align="right">Forfall</TableCell>
                    <TableCell align="right">Beløp</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRows.map((row) => (
                    <TableRow
                      key={row.name}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        {row.name}
                      </TableCell>
                      <TableCell align="right">{(row.debtor as string).replace(/\b\w/g, c => c.toUpperCase())}</TableCell>
                      <TableCell align="right">{row.dueDate}</TableCell>
                      <TableCell align="right">{row.amount}</TableCell>
                      <TableCell align="right">{row.status}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination 
                colSpan={3}  
                count={currentRows.length < maxCount ? rows.length : -1} 
                rowsPerPage={maxCount} 
                page={currentPage}
                labelRowsPerPage="Rader per side" 
                labelDisplayedRows={({from, to, count}) => `${from}–${to} av ${count !== -1 ? count : `mer enn ${to}`}`} 
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
        </Grid>
      </Grid>
    )
  }

export default OrderList

  